import { Injectable, signal, WritableSignal } from '@angular/core';
import { UserCredential } from '@angular/fire/auth';
import { SamlAppRole, SamlExtractedInfo } from '@airwallet/shared-models/saml/saml';
import { HttpService, RequestTypes } from '@services/helper/http.service';
import { SamlMemberInfo } from '@airwallet/shared-models/invited-user';
import { Role } from '@airwallet/shared-models/claims';
import { UidName } from '@airwallet/shared-models/misc';
import { PreferencesService } from '@services/account/preferences.service';
import { AuthService } from '@services/auth/auth.service';
import { LocalStorageService } from '@services/local-storage/local-storage.service';
import { ControlledDashboardOwnerUser } from '@airwallet/shared-models/controlled-dashboard-owner-user';
import { AuthenticatedUser, DashboardOwnerUser } from '@dashboard_models/dashboard-owner-user';

@Injectable({
    providedIn: 'root'
})
export class SamlService {
    samlMembershipOwners: WritableSignal<UidName[]> = signal(this.localStorageService.getItem('samlMembershipOwners'));
    loadingNewOwner: WritableSignal<boolean> = signal(false);
    chosenUid: WritableSignal<string> = signal('');

    constructor(
        private http: HttpService,
        private preferencesService: PreferencesService,
        private authService: AuthService,
        private localStorageService: LocalStorageService
    ) {}

    /**
     * Sets up the SAML service by fetching and storing SAML membership owners.
     * This is called from the sidebar component, as it needs to be initialized early.
     * It waits for the AuthService permissions to be ready before proceeding, as it needs the authenticatedUser to be set,
     * which is done in AuthService after sign-in.
     */
    async setupSamlService() {
        await this.authService.permissionsReady;
        const authenticatedUser: AuthenticatedUser = this.localStorageService.getItem('authenticatedUser');
        const dashboardOwnerUser: DashboardOwnerUser = this.localStorageService.getItem('dashboardOwnerUser');
        this.chosenUid.set(dashboardOwnerUser.uid);

        if (!this.localStorageService.getItem('samlMembershipOwners')) {
            this.loadingNewOwner.set(true);
            const uidName: UidName[] = await this.getOwners(authenticatedUser.uid);
            this.localStorageService.setItem('samlMembershipOwners', uidName);
            this.samlMembershipOwners.set(uidName);
            this.loadingNewOwner.set(false);
        }
    }

    /**
     * Extracts roles from a SAML response.
     *
     * Parses the SAML response to find and validate roles. If a role is `owner`,
     * it returns only that role. Otherwise, it returns all valid roles.
     *
     * @param result - The `UserCredential` containing the SAML response.
     * @returns An array of valid `SamlAppRole` objects.
     */
    extractRolesFromSamlResponse(result: UserCredential): SamlAppRole[] {
        const rawInfo = (result as any)._tokenResponse?.rawUserInfo;
        if (rawInfo) {
            const parsed = JSON.parse(rawInfo);

            // Find the property that ends with "/claims/role"
            const roleKey: string = Object.keys(parsed).find(k => k.endsWith('/claims/role'));

            const mappedRoles: SamlAppRole[] = [];
            if (roleKey) {
                let roles = parsed[roleKey] || [];
                const uidRegex = /^(?:[a-zA-Z0-9]{28}|[a-zA-Z0-9]{28}_operated_by_[a-zA-Z0-9]{28})$/; // Regex to match 28-character UID with optional _operated_by_ suffix

                // Ensure roles is always an array
                if (!Array.isArray(roles)) {
                    roles = [roles];
                }

                for (const entry of roles) {
                    const [groupId, role] = entry.split(',');
                    if (uidRegex.test(groupId) && Object.values(Role).includes(role as Role)) {
                        if (role === 'owner') {
                            return [{ groupId, role: role as Role }]; // Return immediately if role is 'owner'
                        }
                        mappedRoles.push({ groupId, role: role as Role });
                    } else {
                        console.warn(`Invalid groupId or role detected: groupId=${groupId}, role=${role}`);
                    }
                }
            }

            console.log('Mapped roles:', mappedRoles);
            return mappedRoles;
        }
        return [];
    }

    /**
     * Extracts SAML info from a parsed info object, ignoring the schema prefix.
     * @param info The object containing SAML parsed info.
     * @returns An object with the extracted values.
     */
    extractInfo(info: Record<string, string>): SamlExtractedInfo {
        let firstName, lastName, phoneNumber: string;

        // Iterate over every key in the object
        for (const key in info) {
            if (Object.prototype.hasOwnProperty.call(info, key)) {
                if (key.endsWith('givenname')) {
                    firstName = info[key];
                } else if (key.endsWith('surname')) {
                    lastName = info[key];
                } else if (key.endsWith('phone')) {
                    phoneNumber = info[key];
                }
            }
        }

        // Process the role claim if it was found

        return { firstName, lastName, phoneNumber };
    }

    async changeDashboard() {
        this.loadingNewOwner.set(true);
        this.localStorageService.setItem('owner', this.chosenUid());
        const { claims } = await this.authService.setLocalStorage(this.authService.authenticatedUser, undefined, this.chosenUid());
        await Promise.all([this.preferencesService.loadPreferences(), this.authService.setDashboardOwnerPermissions(claims)]);
        if (this.authService.isAdmin()) {
            const controlledUser: ControlledDashboardOwnerUser = await this.getControlledDashboardOwnerUser(this.chosenUid());
            this.localStorageService.setItem('controlled_user', controlledUser);
        }
        window.location.reload();
        this.loadingNewOwner.set(false);
    }

    async getControlledDashboardOwnerUser(chosenUid: string): Promise<ControlledDashboardOwnerUser | null> {
        return await this.http.dynamicHttp(`api_support/get_controlled_dashboard_owner_user?owner=${chosenUid}&member=${this.authService.authenticatedUser.uid}`, RequestTypes.GET, { skipOwner: true });
    }

    async validateSamlAccess(roles: SamlAppRole[]): Promise<void> {
        return await this.http.dynamicHttp('api_account/saml/validate', RequestTypes.POST, { body: { samlRoles: roles } });
    }

    async createUserWithSamlResponse(memberInfo: SamlMemberInfo, roles: SamlAppRole[]): Promise<void> {
        return await this.http.dynamicHttp('api_account/saml', RequestTypes.POST, { body: { roles: roles, memberInfo } });
    }

    async getOwners(controlUid?: string): Promise<UidName[]> {
        return await this.http.dynamicHttp(`api_account/saml/owners?controlUid=${controlUid}`, RequestTypes.GET, { skipOwner: true });
    }

    async removePasswordProvider(): Promise<void> {
        return await this.http.dynamicHttp('api_account/saml/remove_password_provider', RequestTypes.GET);
    }
}
